import { useContext, useEffect, useMemo, useState } from 'react'
import { ColumnFiltersState } from '@tanstack/table-core'
import { useParams } from 'react-router-dom'
import { Group, Stack, Switch, Title } from '@mantine/core'
import { BreedDropdown } from '@shared/components/breed-dropdown/breed-dropdown'
import { SeasonDropdown } from '@shared/components/season-dropdown/season-dropdown'
import { StockAgeDropdown } from '@shared/components/stock-age-dropdown/stock-age-dropdown'
import {
  LotDetailsTable,
  ResultOptions,
} from '@shared/components/wool-data/lot-details-table/lot-details-table'
import { GetLotDetailsGql } from '@/graphql/queries/wool-data/get-lot-details.graphql'
import { useAuthenticatedGraphQuery } from '@/hooks/use-authenticated-query'
import { Breed, StationAuthRole, StockAge, WoolSoldStatus } from '@grower-api-types'
import { StationContext } from '@/pages/station-context'

const ResultOptionToWoolSoldStatus = {
  [ResultOptions.Sold]: WoolSoldStatus.SoldOrBin,
  [ResultOptions.Unsold]: WoolSoldStatus.Unsold,
}

export function LotDetails() {
  const [seasonStartDate, setSeasonStartDate] = useState<Date | undefined>(undefined)
  const [viewAllUnsoldLots, setViewAllUnsoldLots] = useState(false)
  const [breed, setBreed] = useState<Breed | null>()
  const [stockAge, setStockAge] = useState<StockAge | null>()

  // Get the station record number from the URL
  const { stationRecordNumber } = useParams()

  // Decide if we should show the financial columns based on the user's role for this station
  const [hideFinancialColumns, setHideFinancialColumns] = useState(true) // Default to true
  const { allowedStations } = useContext(StationContext)
  useEffect(() => {
    if (allowedStations.length > 0 && stationRecordNumber) {
      const selectedStation = allowedStations.find(
        (station) => station.recordNumber === stationRecordNumber
      )

      const canViewFinancialColumns = selectedStation?.roles.includes(StationAuthRole.FullAccess)
      setHideFinancialColumns(!canViewFinancialColumns)
    }
  }, [stationRecordNumber, allowedStations])

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  // Reset column filters when the station record number changes
  useEffect(() => {
    setColumnFilters([])
  }, [stationRecordNumber])

  // Determine what sold result status to filter by
  const soldResultToFilterBy: WoolSoldStatus | null = useMemo(() => {
    // Use the 'view all unsold lots' switch to determine if we should show all unsold lots
    if (viewAllUnsoldLots) {
      return WoolSoldStatus.Unsold
    }

    // Use the column filters to determine
    return ResultOptionToWoolSoldStatus[
      columnFilters.find((dict) => dict.id === 'result')?.value as ResultOptions
    ]
  }, [viewAllUnsoldLots, columnFilters])

  const enableSeasonFilter: boolean = useMemo(() => !viewAllUnsoldLots, [viewAllUnsoldLots])
  const seasonFilterValue: string | undefined = useMemo(() => {
    if (viewAllUnsoldLots) {
      return undefined
    }

    return seasonStartDate?.getFullYear().toString().substring(2)
  }, [viewAllUnsoldLots, seasonStartDate])

  // Get the lot details data
  const { data, isLoading, isError } = useAuthenticatedGraphQuery({
    queryKey: [
      'lotDetailsData',
      stationRecordNumber,
      columnFilters,
      seasonStartDate,
      breed,
      stockAge,
      seasonFilterValue,
      soldResultToFilterBy,
    ],
    gql: GetLotDetailsGql,
    queryParams: {
      stationRecordNumber,
      saleAndSeason: columnFilters.find((dict) => dict.id === 'saleAndSeason')?.value as string,
      season: seasonFilterValue,
      result: soldResultToFilterBy,
      breed,
      stockAge,
    },
    enabled: !!stationRecordNumber,
  })

  return (
    <Stack>
      <Group>
        <Title order={2} data-testid='station-wool-data-lot-details' mr='auto'>
          Lot Data
        </Title>
        <Switch
          label='View all unsold lots'
          checked={viewAllUnsoldLots}
          onChange={(event) => setViewAllUnsoldLots(event.currentTarget.checked)}
        />
        <BreedDropdown setBreed={setBreed} />
        <StockAgeDropdown setStockAge={setStockAge} />
        <SeasonDropdown
          seasonsBeforeCurrent={5}
          seasonsAfterCurrent={1}
          setSeasonStartDate={(val) => {
            if (val instanceof Date && !isNaN(val.getTime())) {
              setSeasonStartDate(val)
            }
          }}
          setSeasonEndDate={() => undefined} // Season end date is currently not used by this page
          enabled={enableSeasonFilter}
        />
      </Group>
      <LotDetailsTable
        data={data?.listLotDetails?.results ?? []}
        reducedView
        hideFinancialColumns={hideFinancialColumns}
        isLoading={isLoading}
        isError={isError}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        enableResultFilter={!viewAllUnsoldLots}
        sumOfTotalBales={data?.listLotDetails?.lotDetailTotals?.bales ?? null}
        sumOfTotalGrossProceeds={data?.listLotDetails?.lotDetailTotals?.grossProceeds ?? null}
        sumOfTotalNetKg={data?.listLotDetails?.lotDetailTotals?.netKgs ?? null}
        weightedAverageOfAirflowMicron={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestMicronAirflow ?? null
        }
        weightedAverageOfCoefficientOfVariationLaserscan={
          data?.listLotDetails?.lotDetailWeightedAverages
            ?.coreTestCoefficientOfVariationLaserscan ?? null
        }
        weightedAverageOfColour={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestColourYZ ?? null
        }
        weightedAverageOfLaserscanMicron={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestMicronLaserscan ?? null
        }
        weightedAverageOfMicron={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestMicron ?? null
        }
        weightedAverageOfOfdaMicron={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestMicronOfda ?? null
        }
        weightedAverageOfPointOfBreakBase={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestPointOfBreakBase ?? null
        }
        weightedAverageOfPointOfBreakMid={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestPointOfBreakMid ?? null
        }
        weightedAverageOfPointOfBreakTip={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestPointOfBreakTip ?? null
        }
        weightedAverageOfPriceClean={
          data?.listLotDetails?.lotDetailWeightedAverages?.priceClean ?? null
        }
        weightedAverageOfPriceGreasy={
          data?.listLotDetails?.lotDetailWeightedAverages?.priceGreasy ?? null
        }
        weightedAverageOfStapleAverageStrength={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestStapleAverageStrength ?? null
        }
        weightedAverageOfStapleLength={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestStapleLengthGreasy ?? null
        }
        weightedAverageOfStapleStrength={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestStapleStrength ?? null
        }
        weightedAverageOfVegetableMatter={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestVegetableMatterBase ?? null
        }
        weightedAverageOfYield={
          data?.listLotDetails?.lotDetailWeightedAverages?.coreTestYield ?? null
        }
      />
    </Stack>
  )
}
